import { motion } from 'framer-motion';
import { Check, ExternalLink } from 'lucide-react';

const PlansPage = () => {
  const plans = [
    {
      name: 'Starter',
      price: '$20',
      period: '/mo',
      features: [
        '1 app build per month',
        'Basic research signals',
        'Standard build time',
        'Community support',
      ],
      isCurrent: false,
    },
    {
      name: 'Pro',
      price: '$50',
      period: '/mo',
      features: [
        '3 app builds per month',
        'Full PRD & design brief',
        'Priority build queue',
        'Email support',
      ],
      isCurrent: true,
    },
    {
      name: 'Business',
      price: '$100',
      period: '/mo',
      features: [
        '10 app builds per month',
        'Custom branding & export',
        'Dedicated support',
        'API access',
      ],
      isCurrent: false,
    },
  ];

  const handleManageSubscription = () => {
    alert('Redirecting to subscription management portal...');
  };

  const handleUpgrade = (planName) => {
    alert(`Upgrading to ${planName} plan...`);
  };

  return (
    <div className="max-w-5xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl font-extrabold text-[#0F172A] mb-2 text-center tracking-[-0.02em]">Plans & Credits</h1>
        <p className="text-[#475569] mb-12 text-center">
          Manage your subscription and credits
        </p>

        {/* Current Plan Card */}
        <div
          className="rounded-2xl bg-white border border-[#E2E8F0] p-8 mb-12 max-w-3xl mx-auto"
          style={{ boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.05)' }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-[#0F172A]">Current Plan</h2>
            <button
              onClick={handleManageSubscription}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-[#E2E8F0] text-[#475569] hover:text-[#2563EB] hover:border-[#2563EB] transition-all text-sm font-semibold"
            >
              <ExternalLink className="w-4 h-4" />
              Manage Subscription
            </button>
          </div>

          <div className="flex items-baseline gap-2 mb-6">
            <span className="text-3xl font-extrabold text-[#0F172A] tracking-[-0.02em]">Pro</span>
            <span className="px-3 py-1 rounded-full bg-[#EFF6FF] text-[#2563EB] text-xs font-bold uppercase tracking-wider">
              Active
            </span>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#475569]">2 / 3 builds used</span>
              <span className="text-[#0F172A] font-semibold">1 remaining</span>
            </div>
            <div className="w-full h-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#2563EB] rounded-full"
                style={{ width: '67%' }}
              />
            </div>
            <div className="flex items-center gap-1 text-xs text-[#94A3B8]">
              <span className="inline-block w-3 h-3">🕒</span>
              Resets in 15d 8h
            </div>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-[#0F172A] mb-8 text-center tracking-[-0.01em]">
            Choose your plan
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-2xl p-6 border ${
                  plan.isCurrent
                    ? 'bg-[#1E3A8A] border-[#1E3A8A] text-white grid-bg-dark'
                    : 'bg-white border-[#E2E8F0]'
                }`}
                style={{ boxShadow: plan.isCurrent ? '0 20px 60px rgba(30, 58, 138, 0.3)' : '0 4px 6px -1px rgba(0,0,0,0.05)' }}
              >
                {plan.isCurrent && (
                  <div className="text-center mb-4">
                    <span
                      className="inline-block px-3 py-1 rounded-full bg-[#2563EB] text-white text-xs font-bold uppercase tracking-wider"
                      style={{ boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.39)' }}
                    >
                      Current Plan
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className={`text-2xl font-bold mb-2 ${plan.isCurrent ? 'text-white' : 'text-[#0F172A]'}`}>{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className={`text-4xl font-extrabold tracking-[-0.02em] ${plan.isCurrent ? 'text-white' : 'text-[#0F172A]'}`}>
                      {plan.price}
                    </span>
                    <span className={`text-sm ${plan.isCurrent ? 'text-white/70' : 'text-[#475569]'}`}>{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${plan.isCurrent ? 'bg-white/20' : 'bg-[#EFF6FF]'}`}>
                        <Check className={`w-2.5 h-2.5 ${plan.isCurrent ? 'text-white' : 'text-[#2563EB]'}`} />
                      </div>
                      <span className={plan.isCurrent ? 'text-white/90' : 'text-[#475569]'}>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleUpgrade(plan.name)}
                  disabled={plan.isCurrent}
                  className={`w-full py-3 rounded-lg font-semibold transition-all ${
                    plan.isCurrent
                      ? 'bg-white/10 text-white cursor-default'
                      : 'bg-[#2563EB] hover:bg-[#1D4ED8] text-white'
                  }`}
                  style={!plan.isCurrent ? { boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.39)' } : {}}
                >
                  {plan.isCurrent ? 'Current Plan' : 'Get Started'}
                </button>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="text-center text-[#475569] text-sm mt-8">
          <p>All plans include access to the latest features and updates.</p>
          <p className="mt-2">Questions? Contact us at hello@1prompt.app</p>
        </div>
      </motion.div>
    </div>
  );
};

export default PlansPage;
